import React, { useState, useCallback, useMemo, useEffect, useRef, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import { 
  BarChart3, 
  Package, 
  Users, 
  FileText, 
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  CreditCard,
  DollarSign,
  Activity,
  Filter,
  ChevronDown
} from 'lucide-react';

const Dashboard = () => {
  const { user, justLoggedIn, clearJustLoggedIn } = useAuth();
  const { theme } = useContext(ThemeContext);
  const { formatCurrency } = useSettings();
  const navigate = useNavigate();
  const [salesFilter, setSalesFilter] = useState('monthly');
  const [purchaseFilter, setPurchaseFilter] = useState('monthly');
  const [topProductsFilter, setTopProductsFilter] = useState('monthly');
  const [topProductsDropdownOpen, setTopProductsDropdownOpen] = useState(false);
  const [financialSummaryIndex, setFinancialSummaryIndex] = useState(0);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const welcomeShownRef = useRef(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (topProductsDropdownOpen && !event.target.closest('.top-products-dropdown')) {
        setTopProductsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [topProductsDropdownOpen]);

  // Filter data based on selected time period
  const getFilteredSalesData = useMemo(() => {
    if (!dashboardData?.monthlySales) return [];
    
    switch (salesFilter) {
      case 'weekly':
        // For weekly, show last 4 weeks of data with week labels
        return dashboardData.monthlySales.slice(-4).map((item, index) => ({
          ...item,
          label: `Week ${4 - index}`
        }));
      case 'annually':
        // For annually, show all 12 months
        return dashboardData.monthlySales;
      default: // monthly
        // For monthly, show last 6 months
        return dashboardData.monthlySales.slice(-6);
    }
  }, [dashboardData?.monthlySales, salesFilter]);

  const getFilteredPurchaseData = useMemo(() => {
    if (!dashboardData?.monthlyPurchases) return [];
    
    switch (purchaseFilter) {
      case 'weekly':
        // For weekly, show last 4 weeks of data with week labels
        return dashboardData.monthlyPurchases.slice(-4).map((item, index) => ({
          ...item,
          label: `Week ${4 - index}`
        }));
      case 'annually':
        // For annually, show all 12 months
        return dashboardData.monthlyPurchases;
      default: // monthly
        // For monthly, show last 6 months
        return dashboardData.monthlyPurchases.slice(-6);
    }
  }, [dashboardData?.monthlyPurchases, purchaseFilter]);

  const getFilteredTopProductsData = useMemo(() => {
    if (!dashboardData?.topSalesProducts) return [];
    
    switch (topProductsFilter) {
      case 'daily':
        // For daily, show top 5 products
        return dashboardData.topSalesProducts.slice(0, 5);
      case 'weekly':
        // For weekly, show top 5 products
        return dashboardData.topSalesProducts.slice(0, 5);
      default: // monthly
        // For monthly, show top 5 products
        return dashboardData.topSalesProducts.slice(0, 5);
    }
  }, [dashboardData?.topSalesProducts, topProductsFilter]);

  // Financial summary data
  const financialSummaries = useMemo(() => {
    if (!dashboardData) return [];
    
    return [
      {
        title: "Total Revenue",
        value: formatCurrency(dashboardData.totalRevenue || '0'),
      change: '+12.5%',
      icon: DollarSign,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        description: "Total sales revenue for the period"
      },
      {
        title: "Net Profit",
        value: formatCurrency(dashboardData.netProfit || '0'),
        change: '+8.2%',
        icon: TrendingUp,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        description: "Net profit after all expenses"
      },
      {
        title: "Total Expenses",
        value: formatCurrency(dashboardData.totalExpenses || '0'),
        change: '-3.1%',
        icon: TrendingDown,
        color: 'text-red-600',
      bgColor: 'bg-red-50',
        description: "Total operational expenses"
      },
      {
        title: "Cash Flow",
        value: formatCurrency(dashboardData.cashFlow || '0'),
        change: '+15.7%',
        icon: Activity,
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
        description: "Net cash flow from operations"
      },
      {
        title: "Accounts Receivable",
        value: formatCurrency(dashboardData.accountsReceivable || '0'),
        change: '+5.3%',
        icon: CreditCard,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        description: "Outstanding customer payments"
      },
      {
        title: "Accounts Payable",
        value: formatCurrency(dashboardData.accountsPayable || '0'),
        change: '-2.8%',
        icon: FileText,
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-50',
        description: "Outstanding supplier payments"
      }
    ];
  }, [dashboardData]);

  // Show welcome message when user just logged in
  useEffect(() => {
    if (justLoggedIn && user && !welcomeShownRef.current) {
      welcomeShownRef.current = true;
      toast.success(`Welcome back, ${user.name || user.username || 'User'}! 🎉`, {
        duration: 3000,
        icon: '👋',
        style: {
          background: '#10B981',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
      clearJustLoggedIn();
    }
  }, [justLoggedIn, user, clearJustLoggedIn]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Use direct API call to ensure we get the data
        const token = localStorage.getItem('auth_token');
        if (token) {
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/dashboard/stats`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setDashboardData(data);
          } else {
            console.error('API request failed:', response.status);
            toast.error('Failed to load dashboard data', {
              duration: 3000,
            });
          }
        } else {
          console.error('No auth token found');
          toast.error('Please log in to view dashboard data', {
            duration: 3000,
          });
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data', {
          duration: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [topProductsFilter]);

  // Key metrics data - using real data from API
  const metrics = useMemo(() => {
    if (!dashboardData) {
      // Return default metrics when data is not available
      return [
        {
          id: 1,
          title: "Today's Sales",
          value: formatCurrency('0'),
          change: '+0%',
          icon: DollarSign,
          bgColor: 'bg-gradient-to-br from-white to-gray-50/50',
          topColor: 'bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500',
          iconBgColor: 'bg-gradient-to-br from-emerald-100 to-emerald-200',
          iconColor: 'text-emerald-700',
          textColor: 'text-gray-900',
          shadowColor: 'shadow-emerald-100'
    },
    {
      id: 2,
          title: "Creditor's Total Amount",
          value: formatCurrency('0'),
          change: '+0%',
          icon: Activity,
          bgColor: 'bg-gradient-to-br from-white to-blue-50/30',
          topColor: 'bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500',
          iconBgColor: 'bg-gradient-to-br from-blue-100 to-blue-200',
          iconColor: 'text-blue-700',
          textColor: 'text-gray-900',
          shadowColor: 'shadow-blue-100'
        },
        {
          id: 3,
          title: "Debtor's Total Amount",
          value: formatCurrency('0'),
          change: '+0%',
          icon: Activity,
          bgColor: 'bg-gradient-to-br from-white to-purple-50/30',
          topColor: 'bg-gradient-to-r from-purple-400 via-purple-500 to-violet-500',
          iconBgColor: 'bg-gradient-to-br from-purple-100 to-purple-200',
          iconColor: 'text-purple-700',
          textColor: 'text-gray-900',
          shadowColor: 'shadow-purple-100'
        },
        {
          id: 4,
          title: 'Approved Orders',
          value: '0',
          change: '+0%',
          icon: ShoppingCart,
          bgColor: 'bg-gradient-to-br from-white to-orange-50/30',
          topColor: 'bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500',
          iconBgColor: 'bg-gradient-to-br from-orange-100 to-orange-200',
          iconColor: 'text-orange-700',
          textColor: 'text-gray-900',
          shadowColor: 'shadow-orange-100'
        },
        {
          id: 5,
          title: 'Pending Approval',
          value: '0',
          change: '+0%',
          icon: ShoppingCart,
          bgColor: 'bg-gradient-to-br from-white to-red-50/30',
          topColor: 'bg-gradient-to-r from-red-400 via-red-500 to-rose-500',
          iconBgColor: 'bg-gradient-to-br from-red-100 to-red-200',
          iconColor: 'text-red-700',
          textColor: 'text-gray-900',
          shadowColor: 'shadow-red-100'
        }
      ];
    }
    
    return [
      {
        id: 1,
        title: "Today's Sales",
        value: formatCurrency(dashboardData.todaysSales || '0'),
        change: '+12.5%',
        icon: DollarSign,
        bgColor: 'bg-gradient-to-br from-white to-gray-50/50',
        topColor: 'bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500',
        iconBgColor: 'bg-gradient-to-br from-emerald-100 to-emerald-200',
        iconColor: 'text-emerald-700',
        textColor: 'text-gray-900',
        shadowColor: 'shadow-emerald-100'
      },
      {
        id: 2,
        title: "Creditor's Total Amount",
        value: formatCurrency(dashboardData.creditorsTotal || '0'),
      change: '+8.2%',
        icon: Activity,
        bgColor: 'bg-gradient-to-br from-white to-blue-50/30',
        topColor: 'bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500',
        iconBgColor: 'bg-gradient-to-br from-blue-100 to-blue-200',
        iconColor: 'text-blue-700',
        textColor: 'text-gray-900',
        shadowColor: 'shadow-blue-100'
      },
      {
        id: 3,
        title: "Debtor's Total Amount",
        value: formatCurrency(dashboardData.debtorsTotal || '0'),
        change: '-5.3%',
        icon: Activity,
        bgColor: 'bg-gradient-to-br from-white to-purple-50/30',
        topColor: 'bg-gradient-to-r from-purple-400 via-purple-500 to-violet-500',
        iconBgColor: 'bg-gradient-to-br from-purple-100 to-purple-200',
        iconColor: 'text-purple-700',
        textColor: 'text-gray-900',
        shadowColor: 'shadow-purple-100'
      },
      {
        id: 4,
        title: 'Approved Orders',
        value: dashboardData.approvedOrders?.toString() || '0',
        change: '+15.7%',
        icon: ShoppingCart,
        bgColor: 'bg-gradient-to-br from-white to-orange-50/30',
        topColor: 'bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500',
        iconBgColor: 'bg-gradient-to-br from-orange-100 to-orange-200',
        iconColor: 'text-orange-700',
        textColor: 'text-gray-900',
        shadowColor: 'shadow-orange-100'
      },
      {
        id: 5,
        title: 'Pending Approval',
        value: dashboardData.pendingApproval?.toString() || '0',
        change: '0%',
        icon: ShoppingCart,
        bgColor: 'bg-gradient-to-br from-white to-red-50/30',
        topColor: 'bg-gradient-to-r from-red-400 via-red-500 to-rose-500',
        iconBgColor: 'bg-gradient-to-br from-red-100 to-red-200',
        iconColor: 'text-red-700',
        textColor: 'text-gray-900',
        shadowColor: 'shadow-red-100'
      },
      {
        id: 6,
        title: 'Received Orders',
        value: dashboardData.receivedOrders?.toString() || '0',
        change: '+5.2%',
        icon: Package,
        bgColor: 'bg-gradient-to-br from-white to-green-50/30',
        topColor: 'bg-gradient-to-r from-green-400 via-green-500 to-emerald-500',
        iconBgColor: 'bg-gradient-to-br from-green-100 to-green-200',
        iconColor: 'text-green-700',
        textColor: 'text-gray-900',
        shadowColor: 'shadow-green-100'
      }
    ];
  }, [dashboardData]);

  // User & Inventory Overview data - using real data from API
  const overviewMetrics = useMemo(() => {
    if (!dashboardData) {
      // Return default overview metrics when data is not available
      return [
        {
          id: 1,
          title: 'Total Users',
          value: '0',
          icon: Users,
          bgColor: 'bg-gradient-to-br from-white to-emerald-50/40',
          topColor: 'bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500',
          iconBgColor: 'bg-gradient-to-br from-emerald-100 to-emerald-200',
          iconColor: 'text-emerald-700',
          textColor: 'text-gray-900',
          shadowColor: 'shadow-emerald-100'
        },
        {
          id: 2,
          title: 'Total Products',
          value: '0',
      icon: Package,
          bgColor: 'bg-gradient-to-br from-white to-blue-50/40',
          topColor: 'bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500',
          iconBgColor: 'bg-gradient-to-br from-blue-100 to-blue-200',
          iconColor: 'text-blue-700',
          textColor: 'text-gray-900',
          shadowColor: 'shadow-blue-100'
    },
    {
      id: 3,
          title: 'Total Customers',
          value: '0',
      icon: Users,
          bgColor: 'bg-gradient-to-br from-white to-purple-50/40',
          topColor: 'bg-gradient-to-r from-purple-400 via-purple-500 to-violet-500',
          iconBgColor: 'bg-gradient-to-br from-purple-100 to-purple-200',
          iconColor: 'text-purple-700',
          textColor: 'text-gray-900',
          shadowColor: 'shadow-purple-100'
    },
    {
      id: 4,
          title: 'Total Suppliers',
          value: '0',
          icon: Package,
          bgColor: 'bg-gradient-to-br from-white to-orange-50/40',
          topColor: 'bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500',
          iconBgColor: 'bg-gradient-to-br from-orange-100 to-orange-200',
          iconColor: 'text-orange-700',
          textColor: 'text-gray-900',
          shadowColor: 'shadow-orange-100'
        }
      ];
    }
    
    return [
      {
        id: 1,
        title: 'Total Users',
        value: dashboardData.totalUsers?.toString() || '0',
        icon: Users,
        bgColor: 'bg-gradient-to-br from-white to-emerald-50/40',
        topColor: 'bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500',
        iconBgColor: 'bg-gradient-to-br from-emerald-100 to-emerald-200',
        iconColor: 'text-emerald-700',
        textColor: 'text-gray-900',
        shadowColor: 'shadow-emerald-100'
      },
      {
        id: 2,
        title: 'Total Products',
        value: dashboardData.totalProducts?.toString() || '0',
        icon: Package,
        bgColor: 'bg-gradient-to-br from-white to-blue-50/40',
        topColor: 'bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500',
        iconBgColor: 'bg-gradient-to-br from-blue-100 to-blue-200',
        iconColor: 'text-blue-700',
        textColor: 'text-gray-900',
        shadowColor: 'shadow-blue-100'
      },
      {
        id: 3,
        title: 'Total Customers',
        value: dashboardData.totalCustomers?.toString() || '0',
        icon: Users,
        bgColor: 'bg-gradient-to-br from-white to-purple-50/40',
        topColor: 'bg-gradient-to-r from-purple-400 via-purple-500 to-violet-500',
        iconBgColor: 'bg-gradient-to-br from-purple-100 to-purple-200',
        iconColor: 'text-purple-700',
        textColor: 'text-gray-900',
        shadowColor: 'shadow-purple-100'
      },
      {
        id: 4,
        title: 'Total Suppliers',
        value: dashboardData.totalSuppliers?.toString() || '0',
        icon: Package,
        bgColor: 'bg-gradient-to-br from-white to-orange-50/40',
        topColor: 'bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500',
        iconBgColor: 'bg-gradient-to-br from-orange-100 to-orange-200',
        iconColor: 'text-orange-700',
        textColor: 'text-gray-900',
        shadowColor: 'shadow-orange-100'
      }
    ];
  }, [dashboardData]);

  // Quick actions data
  const quickActions = [
    {
      id: 1,
      title: 'Retail POS',
      description: 'Start a new retail sale',
      icon: CreditCard,
      href: '/retail-pos',
      gradient: 'from-red-500 to-red-600'
    },
    {
      id: 2,
      title: 'Bulk Sales',
      description: 'Process bulk sales orders',
      icon: Package,
      href: '/bulk-sales',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      id: 3,
      title: 'Proforma Invoice',
      description: 'Create proforma invoices',
      icon: FileText,
      href: '/proforma-invoice',
      gradient: 'from-red-500 to-blue-600'
    },
    {
      id: 4,
      title: 'Credit Note',
      description: 'Issue credit notes',
      icon: TrendingUp,
      href: '/credit-note',
      gradient: 'from-blue-500 to-red-600'
    }
  ];

  // Recent activity data
  const recentActivity = [
    {
      id: 1,
      action: 'New sale completed',
      details: `Retail sale #R-001 for ${formatCurrency('2450.00')}`,
      time: '2 hours ago',
      type: 'sale',
      icon: CreditCard,
      color: 'text-green-600'
    },
    {
      id: 2,
      action: 'Product added',
      details: 'New product "Premium Widget" added to inventory',
      time: '4 hours ago',
      type: 'product',
      icon: Package,
      color: 'text-blue-600'
    },
    {
      id: 3,
      action: 'Customer registered',
      details: 'New customer "John Doe" added to system',
      time: '6 hours ago',
      type: 'customer',
      icon: Users,
      color: 'text-purple-600'
    },
    {
      id: 4,
      action: 'Bulk order processed',
      details: 'Bulk sale #B-005 for ₵12,800.00',
      time: '1 day ago',
      type: 'bulk',
      icon: ShoppingCart,
      color: 'text-orange-600'
    }
  ];


  const handleCardClick = useCallback((href) => {
    console.log(`Navigating to: ${href}`);
    navigate(href);
  }, [navigate]);

  return (
    <Layout>
              
              
              {/* Key Metrics */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mt-16">
                {metrics.map((metric) => (
                  <div key={metric.id} className={`${metric.bgColor} rounded-2xl shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 ease-in-out border border-gray-100/50 overflow-hidden group cursor-pointer backdrop-blur-sm`}>
                    {/* Top Color Bar */}
                    <div className={`${metric.topColor} h-2 w-full group-hover:h-3 transition-all duration-300 shadow-sm`}></div>
                    
                    <div className="p-4 lg:p-6 relative">
                      {/* Subtle background pattern */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      <div className="relative z-10">
                        {/* Icon and Title on same line */}
                        <div className="flex items-center space-x-3 mb-3 lg:mb-4">
                          <div className={`w-10 h-10 lg:w-12 lg:h-12 ${metric.iconBgColor} rounded-xl flex items-center justify-center group-hover:scale-125 group-hover:rotate-3 transition-all duration-300 ease-out shadow-lg flex-shrink-0`}>
                            <metric.icon size={20} className={`${metric.iconColor} lg:w-6 lg:h-6 drop-shadow-sm`} />
                          </div>
                          <h3 className={`${metric.textColor} text-sm lg:text-base font-semibold tracking-wide`}>{metric.title}</h3>
                        </div>
                        {/* Value on next line */}
                        <p className={`${metric.textColor} text-2xl lg:text-3xl font-bold tracking-tight`}>{metric.value}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Chart Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-xl p-4 lg:p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ease-in-out border overflow-hidden group cursor-pointer`}>
                  <div className="flex items-center justify-between mb-4 lg:mb-6">
                    <h3 className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} text-base lg:text-lg font-semibold group-hover:text-blue-600 transition-colors duration-300`}>Monthly Sales</h3>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 lg:w-3 lg:h-3 bg-blue-500 rounded-full group-hover:scale-125 group-hover:animate-pulse transition-all duration-300"></div>
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSalesFilter(salesFilter === 'monthly' ? 'weekly' : salesFilter === 'weekly' ? 'annually' : 'monthly');
                          }}
                          className={`flex items-center space-x-1 px-2 py-1 text-xs ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} rounded-md transition-colors duration-200`}
                        >
                          <Filter size={12} className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />
                          <span className={`${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} font-medium capitalize`}>{salesFilter}</span>
                          <ChevronDown size={10} className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="h-48 lg:h-64 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-100 group-hover:border-blue-200 group-hover:shadow-inner transition-all duration-300 p-4">
                    {loading ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                                         ) : getFilteredSalesData.length > 0 ? (
                       <svg className="w-full h-full" viewBox="0 0 300 200" preserveAspectRatio="none">
                         <defs>
                           <linearGradient id="salesGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                             <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3"/>
                             <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.05"/>
                           </linearGradient>
                         </defs>
                         
                         {/* Grid lines */}
                         <line x1="0" y1="40" x2="300" y2="40" stroke="#E5E7EB" strokeWidth="1" opacity="0.5"/>
                         <line x1="0" y1="80" x2="300" y2="80" stroke="#E5E7EB" strokeWidth="1" opacity="0.5"/>
                         <line x1="0" y1="120" x2="300" y2="120" stroke="#E5E7EB" strokeWidth="1" opacity="0.5"/>
                         <line x1="0" y1="160" x2="300" y2="160" stroke="#E5E7EB" strokeWidth="1" opacity="0.5"/>
                         
                         {/* Calculate max value for scaling */}
                         {(() => {
                           const maxValue = Math.max(...getFilteredSalesData.map(item => parseFloat(item.revenue) || 0));
                           const scale = maxValue > 0 ? 160 / maxValue : 1;
                           const spacing = 300 / (getFilteredSalesData.length + 1);
                           
                           // Generate path data
                           const pathData = getFilteredSalesData.map((item, index) => {
                             const x = spacing + (index * spacing);
                             const revenueValue = parseFloat(item.revenue) || 0;
                             const y = 200 - (revenueValue * scale);
                             return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
                           }).join(' ');
                           
                           // Generate area fill path
                           const areaPath = pathData + ` L ${spacing + (getFilteredSalesData.length - 1) * spacing} 200 L ${spacing} 200 Z`;
                           
                           return (
                             <>
                               {/* Area fill */}
                               <path 
                                 d={areaPath}
                                 fill="url(#salesGradient)"
                                 className="group-hover:opacity-80 transition-opacity duration-300"
                               />
                               
                               {/* Sales data line */}
                               <path 
                                 d={pathData}
                                 stroke="#3B82F6" 
                                 strokeWidth="3" 
                                 fill="none" 
                                 strokeLinecap="round" 
                                 strokeLinejoin="round"
                                 className="group-hover:stroke-blue-600 transition-colors duration-300"
                               />
                               
                               {/* Data points with tooltips */}
                               {getFilteredSalesData.map((item, index) => {
                                 const x = spacing + (index * spacing);
                                 const revenueValue = parseFloat(item.revenue) || 0;
                                 const y = 200 - (revenueValue * scale);
                                 return (
                                   <g key={index}>
                                     <circle 
                                       cx={x} 
                                       cy={y} 
                                       r="3" 
                                       fill="#3B82F6" 
                                       className="group-hover:scale-125 transition-transform duration-300 cursor-pointer"
                                       onMouseEnter={(e) => {
                                         const tooltip = document.createElement('div');
                                         tooltip.className = 'absolute bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg z-50';
                                         const label = salesFilter === 'weekly' ? item.label : item.month;
                                         tooltip.innerHTML = `
                                           <div class="font-semibold">${label}</div>
                                           <div>₵${(revenueValue / 1000).toFixed(1)}K</div>
                                         `;
                                         tooltip.style.left = `${e.clientX + 10}px`;
                                         tooltip.style.top = `${e.clientY - 30}px`;
                                         tooltip.id = 'sales-tooltip';
                                         document.body.appendChild(tooltip);
                                       }}
                                       onMouseLeave={() => {
                                         const tooltip = document.getElementById('sales-tooltip');
                                         if (tooltip) tooltip.remove();
                                       }}
                                     />
                                   </g>
                                 );
                               })}
                               
                               {/* Y-axis labels */}
                               <text x="5" y="45" fontSize="10" fill="#6B7280" className="text-xs">₵{(maxValue * 0.75 / 1000).toFixed(0)}K</text>
                               <text x="5" y="85" fontSize="10" fill="#6B7280" className="text-xs">₵{(maxValue * 0.5 / 1000).toFixed(0)}K</text>
                               <text x="5" y="125" fontSize="10" fill="#6B7280" className="text-xs">₵{(maxValue * 0.25 / 1000).toFixed(0)}K</text>
                               <text x="5" y="165" fontSize="10" fill="#6B7280" className="text-xs">₵0</text>
                               
                               {/* X-axis month/week labels */}
                               {getFilteredSalesData.map((item, index) => {
                                 const x = spacing + (index * spacing);
                                 const label = salesFilter === 'weekly' ? item.label : item.month;
                                 return (
                                   <text 
                                     key={index}
                                     x={x} 
                                     y="185" 
                                     fontSize="9" 
                                     fill="#6B7280" 
                                     textAnchor="middle" 
                                     className="text-xs"
                                   >
                                     {label}
                                   </text>
                                 );
                               })}
                             </>
                           );
                         })()}
                       </svg>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-blue-600 text-sm">
                        No data available
                      </div>
                    )}
                  </div>
                </div>
                                <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-xl p-4 lg:p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ease-in-out border overflow-hidden group cursor-pointer`}>
                  <div className="flex items-center justify-between mb-4 lg:mb-6">
                    <h3 className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} text-base lg:text-lg font-semibold group-hover:text-green-600 transition-colors duration-300`}>Monthly Purchases</h3>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 lg:w-3 lg:h-3 bg-green-500 rounded-full group-hover:scale-125 group-hover:animate-pulse transition-all duration-300"></div>
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPurchaseFilter(purchaseFilter === 'monthly' ? 'weekly' : purchaseFilter === 'weekly' ? 'annually' : 'monthly');
                          }}
                          className={`flex items-center space-x-1 px-2 py-1 text-xs ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} rounded-md transition-colors duration-200`}
                        >
                          <Filter size={12} className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />
                          <span className={`${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} font-medium capitalize`}>{purchaseFilter}</span>
                          <ChevronDown size={10} className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="h-48 lg:h-64 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-100 group-hover:border-green-200 group-hover:shadow-inner transition-all duration-300 p-4">
                    {loading ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                      </div>
                    ) : getFilteredPurchaseData.length > 0 ? (
                      <svg className="w-full h-full" viewBox="0 0 300 240" preserveAspectRatio="none">
                        <defs>
                          <linearGradient id="purchaseGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#10B981" stopOpacity="0.8"/>
                            <stop offset="100%" stopColor="#10B981" stopOpacity="0.4"/>
                          </linearGradient>
                        </defs>
                        
                        {/* Grid lines */}
                        <line x1="0" y1="40" x2="300" y2="40" stroke="#E5E7EB" strokeWidth="1" opacity="0.5"/>
                        <line x1="0" y1="80" x2="300" y2="80" stroke="#E5E7EB" strokeWidth="1" opacity="0.5"/>
                        <line x1="0" y1="120" x2="300" y2="120" stroke="#E5E7EB" strokeWidth="1" opacity="0.5"/>
                        <line x1="0" y1="160" x2="300" y2="160" stroke="#E5E7EB" strokeWidth="1" opacity="0.5"/>
                        <line x1="0" y1="200" x2="300" y2="200" stroke="#E5E7EB" strokeWidth="1" opacity="0.5"/>
                        
                        {/* Calculate max value for scaling */}
                        {(() => {
                          const maxValue = Math.max(...getFilteredPurchaseData.map(item => Math.max(parseFloat(item.approved) || 0, parseFloat(item.pending) || 0)));
                          const scale = maxValue > 0 ? 160 / maxValue : 1;
                          const spacing = 300 / (getFilteredPurchaseData.length + 1);
                          const barWidth = Math.min(20, spacing * 0.6);
                          
                          return (
                            <>
                                                             {/* Bars with tooltips */}
                               {getFilteredPurchaseData.map((item, index) => {
                                 const x = spacing + (index * spacing) - barWidth/2;
                                 const approvedValue = parseFloat(item.approved) || 0;
                                 const pendingValue = parseFloat(item.pending) || 0;
                                 const approvedHeight = approvedValue * scale;
                                 const pendingHeight = pendingValue * scale;
                                 const label = purchaseFilter === 'weekly' ? item.label : item.month;
                                 
                                 return (
                                   <g key={index}>
                                     {/* Approved purchases bar */}
                                     <rect 
                                       x={x} 
                                       y={240 - approvedHeight} 
                                       width={barWidth} 
                                       height={approvedHeight} 
                                       fill="url(#purchaseGradient)"
                                       rx="2"
                                       className="group-hover:opacity-80 transition-opacity duration-300 cursor-pointer"
                                       onMouseEnter={(e) => {
                                         const tooltip = document.createElement('div');
                                         tooltip.className = 'absolute bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg z-50';
                                         tooltip.innerHTML = `
                                           <div class="font-semibold">${label}</div>
                                           <div class="text-green-400">Approved: ₵${(approvedValue / 1000).toFixed(1)}K</div>
                                           <div class="text-red-400">Pending: ₵${(pendingValue / 1000).toFixed(1)}K</div>
                                         `;
                                         tooltip.style.left = `${e.clientX + 10}px`;
                                         tooltip.style.top = `${e.clientY - 30}px`;
                                         tooltip.id = 'purchase-tooltip';
                                         document.body.appendChild(tooltip);
                                       }}
                                       onMouseLeave={() => {
                                         const tooltip = document.getElementById('purchase-tooltip');
                                         if (tooltip) tooltip.remove();
                                       }}
                                     />
                                     
                                     {/* Pending purchases bar */}
                                     <rect 
                                       x={x + barWidth/2} 
                                       y={240 - pendingHeight} 
                                       width={barWidth/2} 
                                       height={pendingHeight} 
                                       fill="#EF4444"
                                       opacity="0.7"
                                       rx="1"
                                       className="group-hover:opacity-90 transition-opacity duration-300 cursor-pointer"
                                       onMouseEnter={(e) => {
                                         const tooltip = document.createElement('div');
                                         tooltip.className = 'absolute bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg z-50';
                                         tooltip.innerHTML = `
                                           <div class="font-semibold">${label}</div>
                                           <div class="text-green-400">Approved: ₵${(approvedValue / 1000).toFixed(1)}K</div>
                                           <div class="text-red-400">Pending: ₵${(pendingValue / 1000).toFixed(1)}K</div>
                                         `;
                                         tooltip.style.left = `${e.clientX + 10}px`;
                                         tooltip.style.top = `${e.clientY - 30}px`;
                                         tooltip.id = 'purchase-tooltip';
                                         document.body.appendChild(tooltip);
                                       }}
                                       onMouseLeave={() => {
                                         const tooltip = document.getElementById('purchase-tooltip');
                                         if (tooltip) tooltip.remove();
                                       }}
                                     />
                                     
                                     {/* Month/Week labels */}
                                     <text 
                                       x={x + barWidth/2} 
                                       y="225" 
                                       fontSize="10" 
                                       fill="#374151" 
                                       textAnchor="middle" 
                                       className="text-xs font-medium"
                                       style={{ fontWeight: '500' }}
                                     >
                                       {label}
                                     </text>
                                   </g>
                                 );
                               })}
                              
                              {/* Y-axis labels */}
                              <text x="5" y="45" fontSize="10" fill="#6B7280" className="text-xs">₵{(maxValue * 0.75 / 1000).toFixed(0)}K</text>
                              <text x="5" y="85" fontSize="10" fill="#6B7280" className="text-xs">₵{(maxValue * 0.5 / 1000).toFixed(0)}K</text>
                              <text x="5" y="125" fontSize="10" fill="#6B7280" className="text-xs">₵{(maxValue * 0.25 / 1000).toFixed(0)}K</text>
                              <text x="5" y="165" fontSize="10" fill="#6B7280" className="text-xs">₵0</text>
                            </>
                          );
                        })()}
                      </svg>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-green-600 text-sm">
                        No data available
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* User & Inventory Overview */}
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                  {overviewMetrics.map((metric) => (
                    <div key={metric.id} className={`${metric.bgColor} rounded-2xl shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 ease-in-out border border-gray-100/50 overflow-hidden group cursor-pointer backdrop-blur-sm`}>
                      {/* Top Color Bar */}
                      <div className={`${metric.topColor} h-2 w-full group-hover:h-3 transition-all duration-300 shadow-sm`}></div>
                      
                      <div className="p-2 lg:p-3 relative">
                        {/* Subtle background pattern */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        
                        <div className="flex items-center justify-between mb-1 lg:mb-2 relative z-10">
                          <div className={`w-5 h-5 lg:w-6 lg:h-6 ${metric.iconBgColor} rounded-xl flex items-center justify-center group-hover:scale-125 group-hover:rotate-3 transition-all duration-300 ease-out shadow-lg`}>
                            <metric.icon size={12} className={`${metric.iconColor} lg:w-3 lg:h-3 drop-shadow-sm`} />
                  </div>
                </div>
                        <div className="relative z-10">
                          <h3 className={`${metric.textColor} text-sm lg:text-base font-semibold mb-1 tracking-wide`}>{metric.title}</h3>
                          <p className={`${metric.textColor} text-xl lg:text-2xl font-bold tracking-tight`}>{metric.value}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Placeholder Chart Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                                                  <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-xl p-4 lg:p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ease-in-out border overflow-hidden group cursor-pointer`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} text-base lg:text-lg font-semibold group-hover:text-orange-600 transition-colors duration-300`}>Top 5 Sales Products</h3>
                      <div className="flex items-center space-x-2">
                        <div className="relative top-products-dropdown">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setTopProductsDropdownOpen(!topProductsDropdownOpen);
                            }}
                            className={`flex items-center space-x-1 px-3 py-1.5 text-xs ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} rounded-lg transition-colors duration-200 font-medium`}
                          >
                            <span className="text-gray-700 capitalize">This {topProductsFilter === 'daily' ? 'Day' : topProductsFilter === 'weekly' ? 'Week' : 'Month'}</span>
                            <ChevronDown size={10} className={`text-gray-600 transition-transform duration-200 ${topProductsDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>
                          
                          {/* Dropdown Menu */}
                          {topProductsDropdownOpen && (
                            <div className={`absolute right-0 top-full mt-1 w-32 ${theme === 'dark' ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} rounded-lg shadow-lg z-50`}>
                              <div className="py-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTopProductsFilter('daily');
                                    setTopProductsDropdownOpen(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-xs transition-colors duration-150 ${
                                    topProductsFilter === 'daily' 
                                      ? theme === 'dark' 
                                        ? 'text-blue-400 bg-blue-900/50' 
                                        : 'text-blue-600 bg-blue-50' 
                                      : theme === 'dark' 
                                        ? 'text-gray-300 hover:bg-gray-700' 
                                        : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                                >
                                  Today
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTopProductsFilter('weekly');
                                    setTopProductsDropdownOpen(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-xs transition-colors duration-150 ${
                                    topProductsFilter === 'weekly' 
                                      ? theme === 'dark' 
                                        ? 'text-blue-400 bg-blue-900/50' 
                                        : 'text-blue-600 bg-blue-50' 
                                      : theme === 'dark' 
                                        ? 'text-gray-300 hover:bg-gray-700' 
                                        : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                                >
                                  This Week
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTopProductsFilter('monthly');
                                    setTopProductsDropdownOpen(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-xs transition-colors duration-150 ${
                                    topProductsFilter === 'monthly' 
                                      ? theme === 'dark' 
                                        ? 'text-blue-400 bg-blue-900/50' 
                                        : 'text-blue-600 bg-blue-50' 
                                      : theme === 'dark' 
                                        ? 'text-gray-300 hover:bg-gray-700' 
                                        : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                                >
                                  This Month
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {loading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                        </div>
                      ) : getFilteredTopProductsData.length > 0 ? (
                        getFilteredTopProductsData.map((product, index) => {
                          // Use trend data from API or generate random if not available
                          const growthPercentage = product.trend || Math.floor(Math.random() * 25) + 15;
                          

                          
                          // Different background colors for each rank
                          const bgColors = [
                            'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200',
                            'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200',
                            'bg-gradient-to-r from-orange-50 to-red-50 border-orange-200',
                            'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200',
                            'bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200'
                          ];
                          
                          const rankBgColors = [
                            'bg-yellow-100 text-yellow-800',
                            'bg-gray-100 text-gray-800',
                            'bg-orange-100 text-orange-800',
                            'bg-blue-100 text-blue-800',
                            'bg-purple-100 text-purple-800'
                          ];
                          
                          return (
                            <div 
                              key={index} 
                              className={`${bgColors[index]} rounded-2xl p-4 border shadow-sm hover:shadow-md transition-all duration-300`}
                            >
                              <div className="flex items-center justify-between">
                                {/* Rank and Product Details */}
                                <div className="flex items-center space-x-4 flex-1">
                                  {/* Rank Badge */}
                                  <div className={`${rankBgColors[index]} w-8 h-8 rounded-xl flex items-center justify-center font-bold text-sm`}>
                                    {index + 1}
                      </div>
                                  
                                  {/* Product Information */}
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-1 break-words">
                                      {product.name || product.product_name || product.title || `Product ${index + 1}`}
                                    </h4>
                                    <div className="flex items-center space-x-4 text-xs text-gray-600">
                                      <span>
                                        {product.salesDisplay || `${product.cartons || 0}ctns ${product.pieces || 0}pcs`}
                                      </span>
                                      <span className="text-green-600 font-semibold">
                                        ₵{product.revenue ? (product.revenue >= 1000 ? (product.revenue / 1000).toFixed(1) + 'K' : product.revenue.toFixed(0)) : '0'}
                                      </span>
                    </div>
                </div>
              </div>

                                {/* Growth Indicator */}
                                <div className="bg-green-100 rounded-lg px-2 py-1 flex items-center space-x-1">
                                  <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                  <span className="text-green-700 font-medium text-xs">{growthPercentage}%</span>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-700 mb-2">No Sales Data Yet</h3>
                          <p className="text-sm text-gray-500 text-center max-w-xs">
                            Start making sales to see your top performing products here
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                                                                    <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gradient-to-br from-slate-50 via-white to-blue-50'} rounded-3xl p-8 shadow-2xl hover:shadow-3xl hover:-translate-y-3 transition-all duration-700 ease-out border border-white/20 overflow-hidden group cursor-pointer relative`}>
                    {/* Animated Background Elements */}
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/40 via-transparent to-purple-50/40 opacity-80 group-hover:opacity-100 transition-all duration-700"></div>
                    <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-emerald-400/20 to-teal-400/20 rounded-full -translate-y-20 translate-x-20 group-hover:scale-125 group-hover:rotate-12 transition-all duration-700 blur-xl"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-purple-400/20 to-pink-400/20 rounded-full translate-y-16 -translate-x-16 group-hover:scale-125 group-hover:-rotate-12 transition-all duration-700 blur-xl"></div>
                    <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-gradient-to-r from-blue-400/15 to-indigo-400/15 rounded-full -translate-x-12 -translate-y-12 group-hover:scale-150 group-hover:rotate-45 transition-all duration-700 blur-lg"></div>
                    
                    {/* Floating Particles */}
                    <div className="absolute top-4 right-8 w-2 h-2 bg-emerald-400/60 rounded-full animate-pulse group-hover:animate-bounce"></div>
                    <div className="absolute bottom-8 left-12 w-1.5 h-1.5 bg-purple-400/60 rounded-full animate-pulse group-hover:animate-bounce" style={{animationDelay: '0.5s'}}></div>
                    <div className="absolute top-16 left-8 w-1 h-1 bg-blue-400/60 rounded-full animate-pulse group-hover:animate-bounce" style={{animationDelay: '1s'}}></div>
                    
                    {/* Header */}
                    <div className="relative z-10 flex items-center justify-between mb-8">
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <div className="w-4 h-4 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-full animate-pulse shadow-lg"></div>
                          <div className="absolute inset-0 w-4 h-4 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-full animate-ping opacity-75"></div>
                        </div>
                        <div>
                          <h3 className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} text-2xl font-bold group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald-600 group-hover:to-teal-600 transition-all duration-500`}>
                            Financial Summary
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">Your business financial overview</p>
                        </div>
                      </div>
                      {financialSummaries.length > 4 && (
                        <div className="flex items-center space-x-3 bg-white/90 backdrop-blur-md rounded-2xl px-4 py-2 shadow-lg border border-white/50">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFinancialSummaryIndex(prev => 
                                prev === 0 ? financialSummaries.length - 4 : prev - 4
                              );
                            }}
                            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors duration-200 disabled:opacity-50"
                            disabled={financialSummaries.length <= 4}
                          >
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <span className="text-sm font-medium text-gray-700 px-2">
                            {financialSummaries.length > 0 ? `${Math.floor(financialSummaryIndex / 4) + 1}/${Math.ceil(financialSummaries.length / 4)}` : '0/0'}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFinancialSummaryIndex(prev => 
                                prev >= financialSummaries.length - 4 ? 0 : prev + 4
                              );
                            }}
                            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors duration-200 disabled:opacity-50"
                            disabled={financialSummaries.length <= 4}
                          >
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      {loading ? (
                        <div className="h-64 flex items-center justify-center">
                          <div className="flex flex-col items-center space-y-4">
                            <div className="relative">
                              <div className="animate-spin rounded-full h-16 w-16 border-4 border-emerald-200 border-t-emerald-600"></div>
                              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-teal-400 animate-spin" style={{animationDirection: 'reverse', animationDuration: '1.5s'}}></div>
                            </div>
                            <p className="text-sm text-gray-500 font-medium">Loading financial insights...</p>
                          </div>
                        </div>
                      ) : financialSummaries.length > 0 ? (
                        <div className="grid grid-cols-2 gap-6">
                          {/* First Widget */}
                          <div className="group/item bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-xl rounded-2xl p-4 border border-white/60 hover:bg-gradient-to-br hover:from-white hover:to-white/90 hover:shadow-2xl hover:scale-105 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity duration-500"></div>
                            {financialSummaryIndex < financialSummaries.length ? (
                              <>
                                <div className="relative z-10">
                                  <div className="flex items-center space-x-3 mb-2">
                                    <div className={`w-12 h-12 ${financialSummaries[financialSummaryIndex].bgColor} rounded-xl flex items-center justify-center shadow-lg group-hover/item:scale-110 group-hover/item:rotate-3 transition-all duration-500 relative overflow-hidden`}>
                                      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity duration-500"></div>
                                      {(() => {
                                        const IconComponent = financialSummaries[financialSummaryIndex].icon;
                                        return <IconComponent size={20} className={`${financialSummaries[financialSummaryIndex].color} relative z-10`} />;
                                      })()}
                                    </div>
                                    <div className="flex-1">
                                      <h4 className="text-sm font-bold text-gray-900 leading-tight mb-1">
                                        {financialSummaries[financialSummaryIndex].title}
                                      </h4>
                                      <div className="flex items-center space-x-2">
                                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                          financialSummaries[financialSummaryIndex].change.startsWith('+') 
                                            ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200' 
                                            : 'bg-gradient-to-r from-red-100 to-rose-100 text-red-700 border border-red-200'
                                        } shadow-sm`}>
                                          {financialSummaries[financialSummaryIndex].change}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <p className="text-2xl font-bold text-gray-900 mb-2 group-hover/item:text-transparent group-hover/item:bg-clip-text group-hover/item:bg-gradient-to-r group-hover/item:from-gray-900 group-hover/item:to-gray-700 transition-all duration-500">
                                    {financialSummaries[financialSummaryIndex].value}
                                  </p>
                                  <p className="text-xs text-gray-600 leading-relaxed group-hover/item:text-gray-700 transition-colors duration-300">
                                    {financialSummaries[financialSummaryIndex].description}
                                  </p>
                                </div>
                              </>
                            ) : (
                              <div className="flex flex-col items-center justify-center h-24 text-gray-400">
                                <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center mb-2 shadow-lg">
                                  <span className="text-gray-400 text-lg">-</span>
                                </div>
                                <p className="text-xs text-gray-400 font-medium">No data</p>
                              </div>
                            )}
                          </div>
                          
                          {/* Second Widget */}
                          <div className="group/item bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-xl rounded-2xl p-4 border border-white/60 hover:bg-gradient-to-br hover:from-white hover:to-white/90 hover:shadow-2xl hover:scale-105 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity duration-500"></div>
                            {financialSummaryIndex + 1 < financialSummaries.length ? (
                              <>
                                <div className="relative z-10">
                                  <div className="flex items-center space-x-3 mb-2">
                                    <div className={`w-12 h-12 ${financialSummaries[financialSummaryIndex + 1].bgColor} rounded-xl flex items-center justify-center shadow-lg group-hover/item:scale-110 group-hover/item:rotate-3 transition-all duration-500 relative overflow-hidden`}>
                                      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity duration-500"></div>
                                      {(() => {
                                        const IconComponent = financialSummaries[financialSummaryIndex + 1].icon;
                                        return <IconComponent size={20} className={`${financialSummaries[financialSummaryIndex + 1].color} relative z-10`} />;
                                      })()}
                                    </div>
                                    <div className="flex-1">
                                      <h4 className="text-sm font-bold text-gray-900 leading-tight mb-1">
                                        {financialSummaries[financialSummaryIndex + 1].title}
                                      </h4>
                                      <div className="flex items-center space-x-2">
                                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                          financialSummaries[financialSummaryIndex + 1].change.startsWith('+') 
                                            ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200' 
                                            : 'bg-gradient-to-r from-red-100 to-rose-100 text-red-700 border border-red-200'
                                        } shadow-sm`}>
                                          {financialSummaries[financialSummaryIndex + 1].change}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <p className="text-2xl font-bold text-gray-900 mb-2 group-hover/item:text-transparent group-hover/item:bg-clip-text group-hover/item:bg-gradient-to-r group-hover/item:from-gray-900 group-hover/item:to-gray-700 transition-all duration-500">
                                    {financialSummaries[financialSummaryIndex + 1].value}
                                  </p>
                                  <p className="text-xs text-gray-600 leading-relaxed group-hover/item:text-gray-700 transition-colors duration-300">
                                    {financialSummaries[financialSummaryIndex + 1].description}
                                  </p>
                                </div>
                              </>
                            ) : (
                              <div className="flex flex-col items-center justify-center h-24 text-gray-400">
                                <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center mb-2 shadow-lg">
                                  <span className="text-gray-400 text-lg">-</span>
                                </div>
                                <p className="text-xs text-gray-400 font-medium">No data</p>
                              </div>
                            )}
                          </div>

                          {/* Third Widget */}
                          <div className="group/item bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-white/50 hover:bg-white/90 hover:shadow-lg transition-all duration-300">
                            {financialSummaryIndex + 2 < financialSummaries.length ? (
                              <>
                                <div className="flex items-center space-x-3 mb-3">
                                  <div className={`w-12 h-12 ${financialSummaries[financialSummaryIndex + 2].bgColor} rounded-xl flex items-center justify-center shadow-lg group-hover/item:scale-110 transition-transform duration-300`}>
                                    {(() => {
                                      const IconComponent = financialSummaries[financialSummaryIndex + 2].icon;
                                      return <IconComponent size={24} className={financialSummaries[financialSummaryIndex + 2].color} />;
                                    })()}
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="text-sm font-bold text-gray-900 leading-tight">
                                      {financialSummaries[financialSummaryIndex + 2].title}
                                    </h4>
                                    <div className="flex items-center space-x-2 mt-1">
                                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                        financialSummaries[financialSummaryIndex + 2].change.startsWith('+') 
                                          ? 'bg-green-100 text-green-700' 
                                          : 'bg-red-100 text-red-700'
                                      }`}>
                                        {financialSummaries[financialSummaryIndex + 2].change}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <p className="text-2xl font-bold text-gray-900 mb-2">
                                  {financialSummaries[financialSummaryIndex + 2].value}
                                </p>
                                <p className="text-xs text-gray-600 leading-relaxed">
                                  {financialSummaries[financialSummaryIndex + 2].description}
                                </p>
                              </>
                            ) : (
                              <div className="flex flex-col items-center justify-center h-24 text-gray-400">
                                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-2">
                                  <span className="text-gray-400 text-lg">-</span>
                                </div>
                                <p className="text-xs text-gray-400">No data</p>
                              </div>
                            )}
                          </div>

                          {/* Fourth Widget */}
                          <div className="group/item bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-white/50 hover:bg-white/90 hover:shadow-lg transition-all duration-300">
                            {financialSummaryIndex + 3 < financialSummaries.length ? (
                              <>
                                <div className="flex items-center space-x-3 mb-3">
                                  <div className={`w-12 h-12 ${financialSummaries[financialSummaryIndex + 3].bgColor} rounded-xl flex items-center justify-center shadow-lg group-hover/item:scale-110 transition-transform duration-300`}>
                                    {(() => {
                                      const IconComponent = financialSummaries[financialSummaryIndex + 3].icon;
                                      return <IconComponent size={24} className={financialSummaries[financialSummaryIndex + 3].color} />;
                                    })()}
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="text-sm font-bold text-gray-900 leading-tight">
                                      {financialSummaries[financialSummaryIndex + 3].title}
                                    </h4>
                                    <div className="flex items-center space-x-2 mt-1">
                                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                        financialSummaries[financialSummaryIndex + 3].change.startsWith('+') 
                                          ? 'bg-green-100 text-green-700' 
                                          : 'bg-red-100 text-red-700'
                                      }`}>
                                        {financialSummaries[financialSummaryIndex + 3].change}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <p className="text-2xl font-bold text-gray-900 mb-2">
                                  {financialSummaries[financialSummaryIndex + 3].value}
                                </p>
                                <p className="text-xs text-gray-600 leading-relaxed">
                                  {financialSummaries[financialSummaryIndex + 3].description}
                                </p>
                              </>
                            ) : (
                              <div className="flex flex-col items-center justify-center h-24 text-gray-400">
                                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-2">
                                  <span className="text-gray-400 text-lg">-</span>
                                </div>
                                <p className="text-xs text-gray-400">No data</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="h-48 flex items-center justify-center">
                          <div className="flex flex-col items-center space-y-3 text-gray-500">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            </div>
                            <p className="text-sm font-medium">No financial data available</p>
                            <p className="text-xs text-gray-400">Data will appear here when available</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
              </div>

              {/* Quick Actions Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 border overflow-hidden`}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full animate-pulse"></div>
                      <h3 className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} text-xl font-bold`}>
                        Quick Actions
                      </h3>
                    </div>
                    <p className="text-sm text-gray-500">Access POS operations quickly</p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {quickActions.map((action) => (
                      <button
                        key={action.id}
                        onClick={() => handleCardClick(action.href)}
                        className="group p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 relative overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative z-10 flex items-center space-x-3">
                          <div className={`w-12 h-12 bg-gradient-to-r ${action.gradient} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                            <action.icon size={24} className="text-white" />
                          </div>
                          <div className="text-left">
                            <h4 className="font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
                              {action.title}
                            </h4>
                            <p className="text-sm text-gray-600">{action.description}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 border overflow-hidden`}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
                      <h3 className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} text-xl font-bold`}>
                        Recent Activity
                      </h3>
                    </div>
                    <p className="text-sm text-gray-500">Latest system activities</p>
                  </div>
                  
                  <div className="space-y-4">
                    {recentActivity.map((activity) => (
                      <div key={activity.id} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                        <div className={`w-10 h-10 ${activity.color.replace('text-', 'bg-').replace('-600', '-100')} rounded-lg flex items-center justify-center`}>
                          <activity.icon size={20} className={activity.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{activity.action}</p>
                          <p className="text-xs text-gray-600 truncate">{activity.details}</p>
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">{activity.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
    </Layout>
  );
};

export default Dashboard;
